import {ARCHIMATE_ELEMENT, ARCHIMATE_RELATIONSHIP} from '../metamodel/Concept';
import {toModdleValue} from '../util/PropertyUtil';
import {JUNCTION, AND_JUNCTION, OR_JUNCTION} from '../util/ModelUtil';

/**
 * Builds moddle Model from our server JSON format.
 * Used by importJSON to load diagrams without XML.
 *
 * Server JSON structure:
 * - identifier, name, version
 * - elements: [{ identifier, name, type }]
 * - relationships: [{ identifier, name, type, source, target }]
 * - organizations: [...]
 * - diagrams: [{ identifier, name, nodes: [{ identifier, type, x, y, w, h, elementRef, children }], connections: [...] }]
 */
export function buildModelFromJson(moddle, json) {
	if (!json || typeof json === 'string') {
		json = json ? JSON.parse(json) : {};
	}
	const ids = moddle.ids;
	const elementsById = {};
	const elements = json.elements || [];
	const relationships = json.relationships || [];
	const diagrams = json.diagrams || [];
	const modelId = json.identifier || ids.nextPrefixed('id-', 'model');
	const modelName = json.name || 'Model';

	const model = moddle.create('archimate:Model', {
		id: modelId,
		name: modelName,
	});
	ids.claim(modelId, model);
	elementsById[modelId] = model;

	const elementsNode = moddle.create('archimate:Elements', {});
	elementsNode.$parent = model;
	model.elementsNode = elementsNode;
	const propertyDefMap = {};
	if (json.propertyDefinitions?.length) {
		for (const pd of json.propertyDefinitions) {
			const def = moddle.create('archimate:PropertyDefinition', {
				id: pd.identifier,
				name: pd.name || '',
				type: pd.type || 'string',
			});
			ids.claim(pd.identifier, def);
			propertyDefMap[pd.identifier] = def;
		}
		if (model.propertyDefinitionsNode == null) {
			model.propertyDefinitionsNode = moddle.create('archimate:PropertyDefinitions', {
				propertyDefinition: Object.values(propertyDefMap),
			});
		}
	}

	const elementObjs = [];
	for (const el of elements) {
		const id = el.identifier || ids.nextPrefixed('id-', el.type);
		const elemAttrs = {
			id,
			name: el.name || '',
			documentation: el.documentation || '',
			type: el.type,
		};
		if (el.properties?.length) {
			const props = moddle.create('archimate:Properties', {});
			props.property = el.properties.map(p => {
				const ref = p.propertyDefinitionRef;
				const defId = ref && (typeof ref === 'object' ? ref.id : ref);
				const defName = ref && typeof ref === 'object' ? (ref.name ?? ref.id) : defId;
				const defType = ref && typeof ref === 'object' ? (ref.type ?? 'string') : 'string';
				let defRef = defId && propertyDefMap[defId];
				if (!defRef && defId) {
					defRef = moddle.create('archimate:PropertyDefinition', {
						id: defId,
						name: defName || defId,
						type: defType,
					});
					ids.claim(defId, defRef);
					propertyDefMap[defId] = defRef;
				}
				return moddle.create('archimate:Property', {
					propertyDefinitionRef: defRef,
					value: toModdleValue(p.value),
				});
			});
			elemAttrs.properties = props;
		}
		const elem = moddle.create(ARCHIMATE_ELEMENT, elemAttrs);
		ids.claim(id, elem);
		elementsById[id] = elem;
		elem.$parent = elementsNode;
		elementObjs.push(elem);
	}
	elementsNode.element = elementObjs;

	const relationshipsNode = moddle.create('archimate:Relationships', {});
	model.relationshipsNode = relationshipsNode;
	const relObjs = [];
	const elemMap = Object.fromEntries(elementObjs.map(e => [e.id, e]));
	for (const rel of relationships) {
		const id = rel.identifier || ids.nextPrefixed('id-', rel.type);
		const src = elemMap[rel.source];
		const tgt = elemMap[rel.target];
		if (!src || !tgt) continue;
		const relAttrs = {
			id,
			name: rel.name || '',
			documentation: rel.documentation || '',
			type: rel.type,
			source: src,
			target: tgt,
		};
		if (rel.type === 'Access' && rel.accessType) {
			relAttrs.accessType = rel.accessType;
		}
		if (rel.properties?.length) {
			const props = moddle.create('archimate:Properties', {});
			props.property = rel.properties.map(p => {
				const ref = p.propertyDefinitionRef;
				const defId = ref && (typeof ref === 'object' ? ref.id : ref);
				const defName = ref && typeof ref === 'object' ? (ref.name ?? ref.id) : defId;
				const defType = ref && typeof ref === 'object' ? (ref.type ?? 'string') : 'string';
				let defRef = defId && propertyDefMap[defId];
				if (!defRef && defId) {
					defRef = moddle.create('archimate:PropertyDefinition', {
						id: defId,
						name: defName || defId,
						type: defType,
					});
					ids.claim(defId, defRef);
					propertyDefMap[defId] = defRef;
				}
				return moddle.create('archimate:Property', {
					propertyDefinitionRef: defRef,
					value: toModdleValue(p.value),
				});
			});
			relAttrs.properties = props;
		}
		const relObj = moddle.create(ARCHIMATE_RELATIONSHIP, relAttrs);
		ids.claim(id, relObj);
		elementsById[id] = relObj;
		relObj.$parent = relationshipsNode;
		relObjs.push(relObj);
	}
	relationshipsNode.relationship = relObjs;
	// Для вкладки «Связи» в свойствах элемента: связи концепта (incoming/outgoing).
	for (const rel of relObjs) {
		const src = rel.source;
		const tgt = rel.target;
		if (src) {
			if (!src.outgoing) src.outgoing = [];
			src.outgoing.push(rel);
		}
		if (tgt) {
			if (!tgt.incoming) tgt.incoming = [];
			tgt.incoming.push(rel);
		}
	}

	const views = moddle.create('archimate:Views', {});
	views.$parent = model;
	const diagramsObj = moddle.create('archimate:Diagrams', {});
	diagramsObj.$parent = views;
	views.diagrams = diagramsObj;
	model.views = views;

	const viewList = [];
	const relMap = Object.fromEntries(relObjs.map(r => [r.id, r]));
	for (const diag of diagrams) {
		const viewId = diag.identifier || ids.nextPrefixed('id-', 'view');
		const viewAttrs = {
			id: viewId,
			name: diag.name || 'Diagram',
			documentation: diag.documentation || '',
		};
		if (diag.viewpoint != null && String(diag.viewpoint).trim() !== '') {
			viewAttrs.viewpoint = String(diag.viewpoint).trim();
		}
		const view = moddle.create('archimate:View', viewAttrs);
		view.$parent = diagramsObj;
		ids.claim(viewId, view);
		elementsById[viewId] = view;

		const nodes = diag.nodes || [];
		const connections = diag.connections || [];
		const nodeMap = {};
		const viewElements = [];

		function addNode(nodeJson, parent) {
			const id = nodeJson.identifier || ids.nextPrefixed('id-', 'node');
			const elemRef = nodeJson.elementRef ? elemMap[nodeJson.elementRef] : null;
			const isJunction =
				elemRef && [JUNCTION, AND_JUNCTION, OR_JUNCTION].includes(elemRef.type);
			const defaultSize = isJunction ? {w: 20, h: 20} : {w: 120, h: 60};
			const nodeAttrs = {
				id,
				elementRef: elemRef,
				x: nodeJson.x ?? 0,
				y: nodeJson.y ?? 0,
				w: nodeJson.w ?? defaultSize.w,
				h: nodeJson.h ?? defaultSize.h,
			};
			if (!elemRef && nodeJson.type) {
				nodeAttrs.type = nodeJson.type;
			}
			if (nodeJson.label != null) {
				nodeAttrs.label =
					typeof nodeJson.label === 'string' ? nodeJson.label : String(nodeJson.label);
			}
			if (isJunction) {
				nodeAttrs.w = 20;
				nodeAttrs.h = 20;
			}
			if (
				(nodeJson.fillColor && typeof nodeJson.fillColor === 'object') ||
				(nodeJson.lineColor && typeof nodeJson.lineColor === 'object') ||
				nodeJson.lineWidth != null
			) {
				const style = moddle.create('archimate:Style', {});
				if (nodeJson.fillColor && typeof nodeJson.fillColor === 'object') {
					const fc = nodeJson.fillColor;
					style.fillColor = moddle.create('archimate:FillColor', {
						r: fc.r ?? 255,
						g: fc.g ?? 255,
						b: fc.b ?? 255,
						a: fc.a ?? 100,
					});
				}
				if (nodeJson.lineColor && typeof nodeJson.lineColor === 'object') {
					const lc = nodeJson.lineColor;
					style.lineColor = moddle.create('archimate:LineColor', {
						r: lc.r ?? 0,
						g: lc.g ?? 0,
						b: lc.b ?? 0,
						a: lc.a ?? 100,
					});
				}
				if (nodeJson.lineWidth != null) style.lineWidth = nodeJson.lineWidth;
				nodeAttrs.style = style;
			}
			if (nodeJson.textAlign != null && nodeJson.textAlign !== '') {
				nodeAttrs.style = nodeAttrs.style || moddle.create('archimate:Style', {});
				nodeAttrs.style.textAlign = String(nodeJson.textAlign);
			}
			const node = moddle.create('archimate:Node', nodeAttrs);
			ids.claim(id, node);
			elementsById[id] = node;
			node.$parent = parent || view;
			nodeMap[id] = node;
			if (parent) {
				if (!parent.viewElements) parent.viewElements = [];
				parent.viewElements.push(node);
			} else {
				viewElements.push(node);
			}
			if (nodeJson.children && nodeJson.children.length) {
				for (const ch of nodeJson.children) {
					addNode(ch, node);
				}
			}
		}
		for (const n of nodes) {
			addNode(n);
		}

		for (const conn of connections) {
			const id = conn.identifier || ids.nextPrefixed('id-', 'conn');
			const srcId = conn.source != null ? String(conn.source) : null;
			const tgtId = conn.target != null ? String(conn.target) : null;
			const srcNode = srcId ? nodeMap[srcId] : null;
			const tgtNode = tgtId ? nodeMap[tgtId] : null;
			if (!srcNode || !tgtNode) continue;
			const relRef = conn.relationshipRef
				? relMap[conn.relationshipRef] || relObjs.find(r => r.id === conn.relationshipRef)
				: null;
			if (relRef) relRef.$parent = relationshipsNode;
			const attrs = {id};
			if (conn.lineColor && typeof conn.lineColor === 'object') {
				const lc = conn.lineColor;
				const style = moddle.create('archimate:Style', {});
				style.lineColor = moddle.create('archimate:LineColor', {
					r: lc.r ?? 0,
					g: lc.g ?? 0,
					b: lc.b ?? 0,
					a: lc.a ?? 100,
				});
				if (conn.lineWidth != null) style.lineWidth = conn.lineWidth;
				attrs.style = style;
			}
			const connObj = moddle.create('archimate:Connection', attrs);
			connObj.$parent = view;
			connObj.source = srcNode;
			connObj.target = tgtNode;
			connObj.relationshipRef = relRef;
			/* Без relationship в модели тип можно передать только в connection.type (бэкенд хранит в diagram_view_connections.type). */
			connObj.type = relRef ? relRef.type || 'Relationship' : conn.type || 'Line';
			// Подпись связи: из payload (label/name) или из relationshipRef.name (после перезагрузки)
			const connectionLabel =
				conn.label != null && conn.label !== ''
					? String(conn.label)
					: conn.name != null && conn.name !== ''
						? String(conn.name)
						: relRef && relRef.name != null && relRef.name !== ''
							? String(relRef.name)
							: '';
			if (connectionLabel !== '') connObj.label = connectionLabel;
			if (conn.bendpoints && conn.bendpoints.length) {
				const waypoints = moddle.create('archimate:Waypoints', {});
				waypoints.waypoints = conn.bendpoints.map(bp =>
					moddle.create('archimate:Waypoint', {x: bp.x ?? 0, y: bp.y ?? 0})
				);
				waypoints.$parent = connObj;
				connObj.waypointsNode = waypoints;
				for (const wp of waypoints.waypoints) {
					wp.$parent = waypoints;
				}
			}
			ids.claim(id, connObj);
			elementsById[id] = connObj;
			viewElements.push(connObj);
		}
		view.viewElements = viewElements;
		viewList.push(view);
	}
	diagramsObj.viewsList = viewList;

	return {model, elementsById};
}
